import { GridColDef } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useDialog } from './use-dialog';

export type ColumnConfig = {
  field: string;
  headerName: string;
  isVisible: boolean;
};

export const useGridColumnSettings = (
  gridApiRef: MutableRefObject<GridApiCommunity>,
  columns: GridColDef[],
  excludedFields?: GridColDef['field'][],
  onColumnChange?: () => void,
) => {
  const [columnSettings, setColumnSettings] = useState<ColumnConfig[]>([]);

  const {
    isDialogOpen: isSettingsOpen,
    handleOpen: openSettings,
    handleClose: closeSettings,
  } = useDialog(false);

  useEffect(() => {
    if (isSettingsOpen && isNotNullOrUndefined(gridApiRef.current)) {
      const visibleFields = gridApiRef.current.getVisibleColumns().map((column) => column.field);

      const columnSettings = columns
        .filter((column) => !excludedFields?.includes(column.field))
        .map((column) => {
          return {
            field: column.field,
            headerName: column.headerName ?? '',
            isVisible: visibleFields.includes(column.field),
          };
        });

      setColumnSettings(columnSettings);
    }
  }, [gridApiRef.current, isSettingsOpen, columns]);

  const handleColumnVisibilityChange = useCallback(
    (field: string, isVisible: boolean) => {
      if (isNullOrUndefined(gridApiRef.current)) return;

      gridApiRef.current.setColumnVisibility(field, isVisible);

      setColumnSettings((prevSettings) =>
        prevSettings.map((column) => (column.field === field ? { ...column, isVisible } : column)),
      );

      onColumnChange?.();
    },
    [gridApiRef.current, onColumnChange],
  );

  return {
    isSettingsOpen,
    openSettings,
    closeSettings,

    columnSettings,
    handleColumnVisibilityChange,
  };
};
